import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { X, Printer } from "@phosphor-icons/react";

export default function ProjectQrPosterSheet({ onClose }) {
  const [projects, setProjects] = useState([]);
  const gridRef = useRef(null);

  useEffect(() => {
    api.get("/projects").then(({ data }) =>
      setProjects(data.filter((p) => p.status !== "cancelled")
        .sort((a, b) => (a.project_name || a.site_address || "").localeCompare(b.project_name || b.site_address || "")))
    ).catch(() => {});
  }, []);

  const posters = projects.flatMap((p) => {
    const label = p.project_name || p.site_address;
    return [
      { key: p.id + "-signin", project: label, kind: "Worker Sign-In", sub: "Scan to sign in / out of site", url: `${window.location.origin}/sign-in?project=${p.id}`, pin: p.sign_in_pin, pinLabel: "Site PIN" },
      { key: p.id + "-diary", project: label, kind: "Site Diary", sub: "Scan to complete the daily site diary", url: `${window.location.origin}/site-diary?project=${p.id}`, pin: p.site_diary_pin, pinLabel: "Site PIN" },
    ];
  });

  const print = () => {
    const cards = [...(gridRef.current?.querySelectorAll("[data-poster]") || [])].map((el) => {
      const canvas = el.querySelector("canvas");
      const img = canvas ? `<img src="${canvas.toDataURL("image/png")}" width="150" height="150" />` : "";
      return `<div class="card">
        <div class="no">${el.dataset.project}</div>
        <div class="type">${el.dataset.kind}</div>
        ${img}
        <div class="sub">${el.dataset.sub}</div>
        <div class="sub">Site PIN</div>
        <div class="pin">${el.dataset.pin}</div>
      </div>`;
    }).join("");
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    w.document.write(`<html><head><title>EQUIPR — Site QR Posters</title><style>
      body{font-family:Montserrat,Arial,sans-serif;color:#0c100f;margin:10mm}
      h1{font-size:16px;margin:0 0 8px}
      .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:6mm}
      .card{border:2px solid #0c100f;padding:12px;text-align:center;page-break-inside:avoid}
      .no{font-size:16px;font-weight:800} .type{font-size:12px;font-weight:700;color:#b45309;margin-bottom:6px;text-transform:uppercase;letter-spacing:1px}
      .sub{font-size:10px;color:#555;margin-top:4px}
      .pin{font-size:26px;font-weight:800;letter-spacing:6px}
    </style></head><body><h1>EQUIPR Civil — Site Sign-In & Site Diary QR + PIN</h1><div class="grid">${cards}</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-panel border border-border-warm rounded-[14px] overflow-hidden shadow-modal w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="project-qr-poster-sheet">
        <div className="flex items-center justify-between px-5 py-3 bg-equipr-black text-white shrink-0">
          <h3 className="font-bold text-sm">Site QR Posters — {projects.length} projects</h3>
          <div className="flex items-center gap-3">
            <button onClick={print} data-testid="project-qr-poster-print"
              className="h-8 px-3 bg-equipr-yellow text-equipr-black text-xs font-bold rounded-none hover:bg-equipr-yellow-dark inline-flex items-center gap-1.5">
              <Printer size={14} weight="bold" /> Print all
            </button>
            <button onClick={onClose} data-testid="project-qr-poster-close"><X size={18} /></button>
          </div>
        </div>
        <div ref={gridRef} className="flex-1 overflow-auto p-5 grid grid-cols-2 gap-4">
          {posters.map((c) => (
            <div key={c.key} data-poster data-project={c.project} data-kind={c.kind} data-sub={c.sub} data-pin={c.pin || "——"}
              className="border-2 border-equipr-black p-4 text-center" data-testid={`site-poster-${c.key}`}>
              <div className="font-extrabold text-equipr-black">{c.project}</div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-accent-ink mb-2">{c.kind}</div>
              <div className="flex justify-center">
                <QRCodeCanvas value={c.url} size={130} level="M" includeMargin />
              </div>
              <div className="text-[10px] text-gray-500 mt-1">{c.sub}</div>
              <div className="text-[10px] text-gray-500 mt-1">{c.pinLabel}</div>
              <div className="text-lg font-extrabold tracking-[0.3em]">{c.pin || "——"}</div>
            </div>
          ))}
          {projects.length === 0 && <p className="col-span-2 text-center text-gray-400 text-sm py-10">Loading projects…</p>}
        </div>
      </div>
    </div>
  );
}
